import React from 'react';
import { Sparkles, TrendingUp, Users, Clock, AlertTriangle, CheckCircle2, Zap } from 'lucide-react';

const AboutPage: React.FC = () => {
  return (
    <div className="pt-10 pb-20 relative overflow-hidden">
       {/* Ambient Background Lights */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none -z-10">
        <div className="absolute top-[10%] left-[20%] w-[600px] h-[600px] bg-mira-accent/5 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-[20%] right-[10%] w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[100px]"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header Section */}
        <div className="text-center mb-20 animate-fade-in">
          <div className="inline-flex items-center px-3 py-1 rounded-full border border-mira-accent/30 bg-mira-accent/5 mb-6 shadow-[0_0_15px_rgba(26,140,255,0.2)]">
            <span className="w-2 h-2 rounded-full bg-mira-accent mr-2 animate-pulse"></span>
            <span className="text-xs font-bold text-mira-accent tracking-widest uppercase drop-shadow-[0_0_5px_rgba(26,140,255,0.5)]">Nuestra Filosofía</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold mb-6 text-white tracking-tight leading-tight">
            Más que tecnología, <br/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-mira-accent to-cyan-300 drop-shadow-[0_0_20px_rgba(26,140,255,0.3)]">
              Crecimiento Automático
            </span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto font-medium">
            Ayudamos a empresas a dejar de perder tiempo y empezar a ganar velocidad.
          </p>
        </div>

        {/* 3 Pillars Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-24">
          {/* Card 1: Innovación */}
          <div className="group relative p-8 bg-mira-surface/50 border border-mira-border rounded-2xl hover:bg-mira-surface transition-all duration-300 hover:-translate-y-2 hover:border-mira-accent/50 hover:shadow-[0_0_30px_rgba(26,140,255,0.15)]">
            <div className="absolute inset-0 bg-gradient-to-b from-mira-accent/5 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="w-14 h-14 bg-mira-bg rounded-xl border border-mira-border flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
              <Sparkles className="w-7 h-7 text-yellow-400 drop-shadow-[0_0_5px_rgba(250,204,21,0.5)]" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-yellow-200 transition-colors">Innovación</h3>
            <p className="text-gray-400 leading-relaxed">
              Transformamos tareas manuales en procesos automáticos que trabajan por ti: responder mensajes, agendar citas, enviar recordatorios y dar seguimiento.
            </p>
          </div>

          {/* Card 2: Resultados */}
          <div className="group relative p-8 bg-mira-surface/50 border border-mira-border rounded-2xl hover:bg-mira-surface transition-all duration-300 hover:-translate-y-2 hover:border-mira-accent/50 hover:shadow-[0_0_30px_rgba(26,140,255,0.15)]">
            <div className="absolute inset-0 bg-gradient-to-b from-mira-accent/5 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="w-14 h-14 bg-mira-bg rounded-xl border border-mira-border flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
              <TrendingUp className="w-7 h-7 text-green-400 drop-shadow-[0_0_5px_rgba(74,222,128,0.5)]" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-green-200 transition-colors">Resultados</h3>
            <p className="text-gray-400 leading-relaxed">
              Automatizamos para que generes más clientes sin aumentar tu carga de trabajo. Bots que contestan, sistemas que agendan, herramientas que operan solas.
            </p>
          </div>

          {/* Card 3: Acompañamiento */}
          <div className="group relative p-8 bg-mira-surface/50 border border-mira-border rounded-2xl hover:bg-mira-surface transition-all duration-300 hover:-translate-y-2 hover:border-mira-accent/50 hover:shadow-[0_0_30px_rgba(26,140,255,0.15)]">
             <div className="absolute inset-0 bg-gradient-to-b from-mira-accent/5 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="w-14 h-14 bg-mira-bg rounded-xl border border-mira-border flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
              <Users className="w-7 h-7 text-pink-400 drop-shadow-[0_0_5px_rgba(244,114,182,0.5)]" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-pink-200 transition-colors">Acompañamiento</h3>
            <p className="text-gray-400 leading-relaxed">
              Estamos contigo antes, durante y después. Ajustamos, mejoramos y optimizamos para que la automatización crezca con tu negocio.
            </p>
          </div>
        </div>

        {/* Story Section */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center" aria-hidden="true">
            <div className="w-full border-t border-mira-border/50"></div>
          </div>
          <div className="relative flex justify-center">
            <span className="px-4 bg-mira-bg text-sm text-gray-500 font-bold tracking-widest uppercase">Origen</span>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          
          {/* Text Story */}
          <div className="space-y-8 animate-fade-up">
            <h2 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight">
              Nuestra Historia
            </h2>
            
            <div className="bg-mira-accent/10 border-l-4 border-mira-accent p-6 rounded-r-xl shadow-[0_0_20px_rgba(26,140,255,0.1)]">
              <p className="text-xl text-white font-bold italic">
                "Mira-IA nació con una misión clara: que nunca más pierdas un cliente por no responder a tiempo."
              </p>
            </div>

            <div className="space-y-4">
              <p className="text-gray-400 text-lg font-medium">
                Vimos cómo negocios increíbles sufrían por problemas operativos que frenaban su crecimiento:
              </p>
              
              <ul className="space-y-3">
                <li className="flex items-center text-gray-400">
                  <AlertTriangle className="w-5 h-5 text-red-400 mr-3 shrink-0" />
                  No podían contestar WhatsApp rápido
                </li>
                <li className="flex items-center text-gray-400">
                  <Clock className="w-5 h-5 text-red-400 mr-3 shrink-0" />
                  Tenían demasiadas citas para administrar
                </li>
                 <li className="flex items-center text-gray-400">
                  <AlertTriangle className="w-5 h-5 text-red-400 mr-3 shrink-0" />
                  Se les olvidaban recordatorios
                </li>
                <li className="flex items-center text-gray-400">
                  <Clock className="w-5 h-5 text-red-400 mr-3 shrink-0" />
                  Gastaban tiempo en tareas repetitivas
                </li>
              </ul>
            </div>
          </div>

          {/* Solution Highlight Box */}
          <div className="relative animate-fade-up" style={{ animationDelay: '200ms' }}>
            {/* Glow effect */}
            <div className="absolute inset-0 bg-gradient-to-tr from-mira-accent/20 to-purple-500/20 rounded-3xl blur-2xl -z-10"></div>
            
            <div className="bg-mira-surface border border-mira-border p-8 md:p-10 rounded-3xl shadow-2xl relative overflow-hidden">
               <div className="absolute top-0 right-0 p-4 opacity-10">
                 <Zap className="w-32 h-32 text-white" />
               </div>

               <h3 className="text-2xl font-bold text-white mb-6">La Solución Mira-IA</h3>
               <p className="text-lg text-gray-300 mb-8 leading-relaxed font-medium">
                 Hoy ayudamos a negocios a automatizar todo eso. Creamos sistemas que trabajan <span className="text-mira-accent font-extrabold drop-shadow-[0_0_8px_rgba(26,140,255,0.8)]">24/7</span>, no descansan y no fallan.
               </p>

               <div className="flex flex-col gap-4">
                 <div className="flex items-center p-3 bg-mira-bg/50 rounded-lg border border-mira-border/50 hover:border-mira-accent/50 transition-colors">
                   <CheckCircle2 className="w-5 h-5 text-mira-accent mr-3" />
                   <span className="text-white font-semibold">Respuesta Inmediata</span>
                 </div>
                 <div className="flex items-center p-3 bg-mira-bg/50 rounded-lg border border-mira-border/50 hover:border-mira-accent/50 transition-colors">
                   <CheckCircle2 className="w-5 h-5 text-mira-accent mr-3" />
                   <span className="text-white font-semibold">Cero Errores Humanos</span>
                 </div>
                 <div className="flex items-center p-3 bg-mira-bg/50 rounded-lg border border-mira-border/50 hover:border-mira-accent/50 transition-colors">
                   <CheckCircle2 className="w-5 h-5 text-mira-accent mr-3" />
                   <span className="text-white font-semibold">Enfoque Total en Crecer</span>
                 </div>
               </div>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
};

export default AboutPage;